import { useState } from "react";
import { Head, router } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import Drawer from "@/components/drawer";
import {
  NewStepFileForm,
  NewStepTemplateForm,
  StepFileForm,
  StepTemplateForm,
  nextIndex
} from "@/pages/manage/products/manage-step-edit-drawer";
import {
  edit_manage_product_step_path,
  manage_product_path,
  manage_product_step_path,
  manage_products_path
} from "@/routes";

function TemplateCard({ stepTemplate, onEdit, orderControls, disabled }) {
  return (
    <article className="card workspace-step-grid-card h-100">
      <div className="card-body d-flex flex-column">
        {orderControls}
        <div>
          <div className="workspace-card-eyebrow">Skabelon {Number(stepTemplate.index) + 1}</div>
          <h3 className="workspace-card-title">{stepTemplate.template?.name || "Skabelon uden navn"}</h3>
        </div>

        <p className="workspace-card-description">
          Version {stepTemplate.template?.version_number || "–"}
        </p>

        <div className="workspace-step-grid-card__footer mt-auto">
          <div className="d-flex flex-wrap gap-3">
            <span className="workspace-chip">Skabelon</span>
          </div>

          <div className="pt-2">
            <button type="button" className="btn btn-primary w-100" onClick={onEdit} disabled={disabled}>
              Redigér
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function NewTemplateCard({ onEdit, disabled }) {
  return (
    <button type="button" className="card workspace-step-grid-card workspace-add-card h-100 text-start" onClick={onEdit} disabled={disabled}>
      <div className="card-body d-flex flex-column">
        <div className="workspace-card-eyebrow">Ny skabelon</div>
        <h3 className="workspace-card-title">Tilknyt skabelon</h3>

        <p className="workspace-card-description">
          Vælg en skabelon fra det globale bibliotek til dette trin.
        </p>

        <div className="workspace-step-grid-card__footer mt-auto">
          <div className="pt-2">
            <span className="btn btn-primary w-100">Tilknyt skabelon</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function FileCard({ stepTemplate, onEdit, orderControls, disabled }) {
  return (
    <article className="card workspace-step-grid-card h-100">
      <div className="card-body d-flex flex-column">
        {orderControls}
        <div>
          <div className="workspace-card-eyebrow">Fil {Number(stepTemplate.index) + 1}</div>
          <h3 className="workspace-card-title">{stepTemplate.name || "Fil uden navn"}</h3>
        </div>

        <p className="workspace-card-description">{stepTemplate.existing_file_name}</p>

        <div className="workspace-step-grid-card__footer mt-auto">
          <div className="d-flex flex-wrap gap-3">
            <span className="workspace-chip">{stepTemplate.file_type_label || "Fil"}</span>
          </div>

          <div className="pt-2">
            <button type="button" className="btn btn-primary w-100" onClick={onEdit} disabled={disabled}>
              Redigér
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function NewFileCard({ onEdit, disabled }) {
  return (
    <button type="button" className="card workspace-step-grid-card workspace-add-card h-100 text-start" onClick={onEdit} disabled={disabled}>
      <div className="card-body d-flex flex-column">
        <div className="workspace-card-eyebrow">Ny fil</div>
        <h3 className="workspace-card-title">Upload fil</h3>

        <p className="workspace-card-description">
          Tilknyt en generisk fil, som alle organisationer med produktet kan hente.
        </p>

        <div className="workspace-step-grid-card__footer mt-auto">
          <div className="pt-2">
            <span className="btn btn-primary w-100">Upload fil</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function MaterialOrderControls({ name, index, count, disabled, onMove, onDragStart, onDragEnd }) {
  return (
    <div className="workspace-material-order">
      <button
        type="button"
        className="btn btn-light border btn-sm workspace-material-order__handle"
        draggable={!disabled}
        disabled={disabled || count < 2}
        aria-label={`Flyt ${name}. Træk eller brug piletasterne.`}
        title="Træk for at ændre rækkefølgen"
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onKeyDown={(event) => {
          if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) return;
          event.preventDefault();
          onMove(index + (["ArrowUp", "ArrowLeft"].includes(event.key) ? -1 : 1));
        }}
      >
        <i className="bi bi-grip-vertical" aria-hidden="true" />
      </button>
      <div className="d-flex gap-1">
        <button type="button" className="btn btn-light border btn-sm" disabled={disabled || index === 0} onClick={() => onMove(index - 1)} aria-label={`Flyt ${name} frem`} title="Flyt frem">
          <i className="bi bi-arrow-up" aria-hidden="true" />
        </button>
        <button type="button" className="btn btn-light border btn-sm" disabled={disabled || index === count - 1} onClick={() => onMove(index + 1)} aria-label={`Flyt ${name} tilbage`} title="Flyt tilbage">
          <i className="bi bi-arrow-down" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function ManageStepTemplatesIndex({ product, step, templates = [] }) {
  const stepTemplates = step.step_templates_attributes || [];
  const hasAvailableTemplate = templates.some((template) => !template.associated_step_id);
  const showStepNumber = Number(step.index) > 0;
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [isNewDrawerOpen, setIsNewDrawerOpen] = useState(false);
  const [isNewFileDrawerOpen, setIsNewFileDrawerOpen] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const activeTemplate = stepTemplates.find((stepTemplate) => stepTemplate.id === activeTemplateId) || null;

  const clearDrag = () => {
    setDraggedId(null);
    setDropTarget(null);
  };

  const getDropTarget = (event, index) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const stacked = window.matchMedia("(max-width: 767.98px)").matches;
    const after = stacked
      ? event.clientY >= bounds.top + bounds.height / 2
      : event.clientX >= bounds.left + bounds.width / 2;
    const insertionIndex = index + (after ? 1 : 0);
    const sourceIndex = stepTemplates.findIndex((material) => material.id === draggedId);
    // Removing the dragged card shifts insertion positions that follow it.
    const targetIndex = insertionIndex - (sourceIndex < insertionIndex ? 1 : 0);
    if (sourceIndex < 0 || sourceIndex === targetIndex) return null;

    return { id: stepTemplates[index].id, side: after ? "after" : "before", index: targetIndex };
  };

  const showDropTarget = (event, index) => {
    if (!draggedId || savingOrder) return;
    event.preventDefault();
    const target = getDropTarget(event, index);
    event.dataTransfer.dropEffect = target ? "move" : "none";
    setDropTarget(target);
  };

  const moveMaterial = (id, targetIndex) => {
    const sourceIndex = stepTemplates.findIndex((material) => material.id === id);
    if (savingOrder || sourceIndex < 0 || sourceIndex === targetIndex || targetIndex < 0 || targetIndex >= stepTemplates.length) return;

    const orderedMaterials = [...stepTemplates];
    const [material] = orderedMaterials.splice(sourceIndex, 1);
    orderedMaterials.splice(targetIndex, 0, material);
    setSavingOrder(true);

    router.patch(`${manage_product_step_path(product.id, step.id)}/step_templates/reorder`, {
      step_template_ids: orderedMaterials.map((entry) => entry.id)
    }, {
      preserveScroll: true,
      onFinish: () => {
        setSavingOrder(false);
        clearDrag();
      }
    });
  };

  return (
    <>
      <Head title={`Skabeloner og filer for ${step.name}`} />

      <section className="workspace-content">
        <div className="vstack gap-4">
          <section className="card workspace-product-summary">
            <div className="card-body">
              {showStepNumber ? (
                <div className="workspace-card-eyebrow">Trin {Number(step.index)}</div>
              ) : null}
              <h2 className={`workspace-product-summary__title${showStepNumber ? "" : " mt-0"}`}>Skabeloner og filer</h2>
            </div>
          </section>

          {stepTemplates.length > 1 ? (
            <p className="text-secondary mb-0" role="status">
              {savingOrder ? "Gemmer rækkefølge…" : "Træk i håndtaget, eller brug pilene til at flytte skabeloner og filer. Rækkefølgen gemmes automatisk."}
            </p>
          ) : null}

          <div className="row g-4" aria-busy={savingOrder}>
            {stepTemplates.map((stepTemplate, index) => {
              const MaterialCard = stepTemplate.content_kind === "file" ? FileCard : TemplateCard;
              const name = stepTemplate.name || stepTemplate.template?.name || "materiale";

              return (
                <div
                  key={stepTemplate.id}
                  className={`col-md-6 col-xl-4 workspace-material-card${draggedId === stepTemplate.id ? " is-dragging" : ""}${dropTarget?.id === stepTemplate.id ? ` is-drop-${dropTarget.side}` : ""}`}
                  onDragEnter={(event) => showDropTarget(event, index)}
                  onDragOver={(event) => showDropTarget(event, index)}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setDropTarget((current) => current?.id === stepTemplate.id ? null : current);
                    }
                  }}
                  onDrop={(event) => {
                    if (!draggedId || savingOrder) return;
                    event.preventDefault();
                    const target = getDropTarget(event, index);
                    if (target) moveMaterial(draggedId, target.index);
                    clearDrag();
                  }}
                >
                  <MaterialCard
                    stepTemplate={stepTemplate}
                    onEdit={() => setActiveTemplateId(stepTemplate.id)}
                    disabled={savingOrder}
                    orderControls={(
                      <MaterialOrderControls
                        name={name}
                        index={index}
                        count={stepTemplates.length}
                        disabled={savingOrder}
                        onMove={(targetIndex) => moveMaterial(stepTemplate.id, targetIndex)}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", stepTemplate.id);
                          setDraggedId(stepTemplate.id);
                        }}
                        onDragEnd={clearDrag}
                      />
                    )}
                  />
                </div>
              );
            })}

            {hasAvailableTemplate ? (
              <div className="col-md-6 col-xl-4">
                <NewTemplateCard onEdit={() => setIsNewDrawerOpen(true)} disabled={savingOrder} />
              </div>
            ) : null}

            <div className="col-md-6 col-xl-4">
              <NewFileCard onEdit={() => setIsNewFileDrawerOpen(true)} disabled={savingOrder} />
            </div>
          </div>
        </div>
      </section>

      <Drawer
        isOpen={Boolean(activeTemplate)}
        onClose={() => setActiveTemplateId(null)}
        title={activeTemplate?.content_kind === "file" ? activeTemplate.name : (activeTemplate?.template?.name || "Redigér skabelon")}
        size="md"
      >
        {activeTemplate ? (
          <div className="workspace-content workspace-content--drawer">
            <div className="vstack gap-4 workspace-drawer-form-card card card-body">
              {activeTemplate.content_kind === "file" ? (
                <StepFileForm
                  product={product}
                  step={step}
                  stepTemplate={activeTemplate}
                />
              ) : (
                <StepTemplateForm
                  product={product}
                  step={step}
                  stepTemplate={activeTemplate}
                  templates={templates}
                />
              )}
            </div>
          </div>
        ) : null}
      </Drawer>

      <Drawer
        isOpen={isNewFileDrawerOpen}
        onClose={() => setIsNewFileDrawerOpen(false)}
        title="Upload fil"
        size="md"
      >
        <div className="workspace-content workspace-content--drawer">
          <div className="vstack gap-4 workspace-drawer-form-card card card-body">
            <NewStepFileForm
              product={product}
              step={step}
              defaultIndex={nextIndex(stepTemplates)}
              onSuccess={() => setIsNewFileDrawerOpen(false)}
            />
          </div>
        </div>
      </Drawer>

      <Drawer
        isOpen={isNewDrawerOpen}
        onClose={() => setIsNewDrawerOpen(false)}
        title="Tilknyt skabelon"
        size="md"
      >
        <div className="workspace-content workspace-content--drawer">
          <div className="vstack gap-4 workspace-drawer-form-card card card-body">
            <NewStepTemplateForm
              product={product}
              step={step}
              defaultIndex={nextIndex(stepTemplates)}
              templates={templates}
              onSuccess={() => setIsNewDrawerOpen(false)}
            />
          </div>
        </div>
      </Drawer>
    </>
  );
}

ManageStepTemplatesIndex.layout = (page) => {
  const { product, step } = page.props;

  return (
    <AuthLayout
      header={{
        breadcrumbs: [
          { label: "Administration", href: manage_products_path() },
          { label: "Alle produkter", href: manage_products_path() },
          { label: product.name, href: `${manage_product_path(product.id)}/edit` },
          { label: step.name, href: edit_manage_product_step_path(product.id, step.id) },
          { label: "Skabeloner og filer" }
        ],
        title: "Skabeloner og filer",
        actions: [
          {
            href: edit_manage_product_step_path(product.id, step.id),
            label: "Tilbage",
            variant: "outline-secondary"
          }
        ]
      }}
    >
      {page}
    </AuthLayout>
  );
};

export default ManageStepTemplatesIndex;
